import { conform, useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import { invariant } from '@epic-web/invariant'
import {
	json,
	redirect,
	type DataFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import {
	useFetcher,
	useLoaderData,
	useSearchParams,
} from '@remix-run/react'
import { useState } from 'react'
import { AuthenticityTokenInput } from 'remix-utils/csrf/react'
import { getClientIPAddress } from "remix-utils/get-client-ip-address";
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { safeRedirect } from 'remix-utils/safe-redirect'
import { z } from 'zod'
//import AmazonLogo from '#app/assets/amazon_logo.webp'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { CardDescription } from '#app/components/ui/card.tsx'
//import { Checkbox } from '#app/components/ui/checkbox.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { Slider } from '#app/components/ui/slider.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { getGmailUserId, requireAnonymous, sessionKey, signupWithMobileNo } from '#app/utils/auth.server.ts'
import { validateCSRF } from '#app/utils/csrf.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { logger } from '#app/utils/logger'
import { getSourceDeviceType, useIsPending } from '#app/utils/misc.tsx'
import { authSessionStorage } from '#app/utils/session.server.ts'

import { sendUserOnboardedWithFDPortfolioAndInterestRateSlackMessage, sendUserOnboardedWithInviteCodeSlackMessage, sendUserOnboardedWithoutInviteCodeSlackMessage } from '#app/utils/slack.server.ts'
import { splashKey, splashSessionStorage } from '#app/utils/splash.server.ts'
import {
	NameSchema,
	MobileNoSchema
} from '#app/utils/user-validation.ts'
import { useOptionalGmailUser } from '#app/utils/user.ts'
import { verifySessionStorage } from '#app/utils/verification.server.ts'
import { redirectToQueryParam, type VerifyFunctionArgs } from './_authlayout.verify.tsx'

const onboardingMobileSessionKey = 'onboardingMobile'
const defaultCurrentPortfolio = 5
const MIN_PORTFOLIO = 0
const MAX_PORTFOLIO = 25
const defaultCurrentInterest = 6.0
const MIN_INTEREST = 4
const MAX_INTEREST = 9.0
const FIXED_INTEREST = 9
const REFERRAL_CODE_MIN_LENGTH = 3
//const MIN_FD_PORTFOLIO = 200000
// const FD_PORTFOLIO_DEFAULT_AMOUNT = "0"
// const MIN_FD_PORTFOLIO_AMOUNT = 5000
// const MAX_FD_PORTFOLIO_AMOUNT = 50000000

const NameFormIntent = 'name-form'
const GenerateInviteCodeIntent = 'generate-invite-code'
const SignupFormIntent = 'signup-form'
const FdPortfolioIntent = 'fd-portfolio-intent'
const HomeRouteFormIntent = 'home-route'
const InviteCodeLaterFormIntent = 'invite-code-later'

// const toWords = new ToWords({
//   localeCode: 'en-IN',
//   converterOptions: {
// 	currency: true,
// 	ignoreDecimal: true,
// 	ignoreZeroCurrency: true,
// 	doNotAddOnly: false,
// 	currencyOptions: {
// 	  // can be used to override defaults for the selected locale
// 	  name: 'Rupee',
// 	  plural: 'Rupees',
// 	  symbol: '₹',
// 	  fractionalUnit: {
// 		name: 'Paisa',
// 		plural: 'Paise',
// 		symbol: '',
// 	  },
// 	},
//   },
// })

type NameFormActionArgs = {
	request: Request
	formData: FormData
}

const SignupFormSchema = z
	.object({
		mobileNo: MobileNoSchema,
		name: NameSchema,
		email: z.string({ required_error: 'Email is required' })
			.email({ message: 'Email is invalid' })
			.min(3, { message: 'Email is too short' })
			.max(100, { message: 'Email is too long' })
			// users can type the email in any case, but we store it in lowercase
			.transform(value => value.toLowerCase())
			.optional(),
		referralCode: z.string().min(REFERRAL_CODE_MIN_LENGTH, 'Code should be min length of 3').optional(),
		age: z.number(),
        fdPortfolioSize: z.number(),
		fdPortfolioInterest: z.number().min(MIN_INTEREST, 'Interest rate should be 4 - 9% range.').max(MAX_INTEREST, 'Interest rate should be 4 - 9% range.'),
		isNewFdUser: z.enum(['true', 'false']),
		redirectTo: z.string().optional(),
		isInviteGenerate: z.string().optional(),
	})

const NameFormSchema = z
	.object({
		name: NameSchema,
		email: z.string({ required_error: 'Email is required' })
			.email({ message: 'Email is invalid' })
			.min(3, { message: 'Email is too short' })
			.max(100, { message: 'Email is too long' })
			// users can type the email in any case, but we store it in lowercase
			.transform(value => value.toLowerCase())
			.optional(),
		referralCode: z.string().min(REFERRAL_CODE_MIN_LENGTH, 'Code should be min length of 3').optional(),
		redirectTo: z.string().optional(),
	})

const NameAndInviteCodeFormSchema = z
	.object({
		mobileNo: MobileNoSchema,
		name: NameSchema,
		email: z.string({ required_error: 'Email is required' })
			.email({ message: 'Email is invalid' })
			.min(3, { message: 'Email is too short' })
			.max(100, { message: 'Email is too long' })
			// users can type the email in any case, but we store it in lowercase
			.transform(value => value.toLowerCase())
			.optional(),
		referralCode: z.string({required_error: 'Invite Code is mandatory to register'}).min(REFERRAL_CODE_MIN_LENGTH, 'Code should be min length of 3'),
		redirectTo: z.string().optional(),
	})

	const GenerateInviteCodeSchema = z
	.object({
		name: NameSchema,
		referralCode: z.string().min(REFERRAL_CODE_MIN_LENGTH, 'Code should be min length of 3').optional(),
	})

async function requireOnboardingMobile(request: Request) {
	await requireAnonymous(request)
	const verifySession = await verifySessionStorage.getSession(
		request.headers.get('cookie'),
	)
	const mobile = verifySession.get(onboardingMobileSessionKey)
	if (typeof mobile !== 'string' || !mobile) {
		throw redirect('/')
	}
	return mobile
}
export async function loader({ request }: DataFunctionArgs) {
	const mobileNo = await requireOnboardingMobile(request)
	logger.info({
		message: `/auth/onboarding,user/null-${getClientIPAddress(request.headers)}`,
		labels: { origin: 'user_page' },
	})
	return json({ mobileNo })
}

export async function action({ request }: DataFunctionArgs) {
	const mobileNo = await requireOnboardingMobile(request)
	const formData = await request.formData()
	await validateCSRF(formData, request.headers)
	checkHoneypot(formData)

	const intent = formData.get('intent')
	console.log('intent : ', intent, formData.entries())
	switch(intent){
      case NameFormIntent: {

		return NameFormAction({ request, formData })

	  }

	  case HomeRouteFormIntent: {

		return redirect(safeRedirect('/'))
		
	  }

	  case FdPortfolioIntent: {

		return FortfolioFormAction({ request, formData })

	  }

	  case GenerateInviteCodeIntent: {

		return GenerateInviteCodeAction({ request, formData })

	  }

	  case SignupFormIntent: {
		const deviceInfo = `${getSourceDeviceType(request)} - ${request.headers.get('user-agent')}`

		const gmailUserId = await getGmailUserId(request)
		
		const gmailUser = gmailUserId
				? await prisma.gmailUser.findUnique({
					select: {
						id: true,
						name: true,
						givenName: true,
						email: true,
						picture: true,
					},
					where: { id: gmailUserId }
	
				})
				: null

		const submission = await parse(formData, {
			schema: intent =>
				SignupFormSchema.superRefine(async (data, ctx) => {
					// check if user exist
					// const existingUser = await prisma.user.findUnique({
					// 	where: { mobileNo: data.mobileNo },
					// 	select: { id: true },
					// })
					// if (existingUser) {
					// 	ctx.addIssue({
					// 		path: ['mobile no.'],
					// 		code: z.ZodIssueCode.custom,
					// 		message: 'A user already exists with this mobile no',
					// 	})
					// 	return
					// }
					
				}).transform(async data => {
					if (intent !== 'submit') return { ...data, session: null }

					const session = await signupWithMobileNo({ ...data, mobileNo, email: data.email ?? null, referralCode: data.referralCode ?? '', gmailUser, isToGenerateReferralCode: true, deviceInfo: deviceInfo})

					// const referralCode = await prisma.user.findUnique({
					// 	select: {id: true, referralCode: true},
					// 	where: { id: session.userId}
					// })

					// const session = await signup({ ...data, mobileNo, referralCode: data.referralCode ?? '' })

					//slack notification
					await sendUserOnboardedWithFDPortfolioAndInterestRateSlackMessage(data.name, `${data.fdPortfolioSize}`, `${data.fdPortfolioInterest}`)
					
					return { ...data, session }
				}),
			async: true,
		})

		if (submission.intent !== 'submit') {
			return json({ status: 'idle', submission } as const)
		}

		if(submission.intent === 'submit' && submission.error.hasOwnProperty('fdPortfolioInterest')){

			//submission.error[''] = [response?.msg ?? 'Something Wrong, try again later.']
			submission.error[''] = [submission.error['fdPortfolioInterest'][0]]
			return json({ status: 'error', submission } as const, { status: 400 })
		}

		if (!submission.value?.session) {
			return json({ status: 'error', submission } as const, { status: 400 })
		}

		// if (!submission.value?.referralCode) {
		// 	return json({ status: 'error', submission } as const, { status: 400 })
		// }

		const { session, redirectTo, fdPortfolioSize, fdPortfolioInterest } = submission.value

		// if(isNewFdUser === 'true'){

		// 	const authSession = await authSessionStorage.getSession(
		// 		request.headers.get('cookie'),
		// 	)

		// 	authSession.set(sessionKey, session.id)
		// 	const verifySession = await verifySessionStorage.getSession()
		// 	const headers = new Headers()
		// 	headers.append(
		// 		'set-cookie',
		// 		await authSessionStorage.commitSession(authSession, {
		// 			expires: session.expirationDate
		// 		}),
		// 	)
		// 	headers.append(
		// 		'set-cookie',
		// 		await verifySessionStorage.destroySession(verifySession),
		// 	)

		// 	return redirect(safeRedirect(redirectTo), {headers})

		// }else{

			const additionalGain = ((Number(fdPortfolioSize)*(FIXED_INTEREST - Number(fdPortfolioInterest)) * 100000) / 100)

			const authSession = await authSessionStorage.getSession(
				request.headers.get('cookie'),
			)

			const splashSession = await splashSessionStorage.getSession(
				request.headers.get('cookie')
			)
			splashSession.flash(splashKey, additionalGain)
			authSession.set(sessionKey, session.id)
			const verifySession = await verifySessionStorage.getSession()
			const headers = new Headers()
			headers.append(
				'set-cookie',
				await authSessionStorage.commitSession(authSession, {
					expires: session.expirationDate
				}),
			)
			headers.append(
				'set-cookie',
				await splashSessionStorage.commitSession(splashSession),
			)
			headers.append(
				'set-cookie',
				await verifySessionStorage.destroySession(verifySession),
			)

			return redirect(safeRedirect(redirectTo), {headers})

	    // }

		// return redirectWithToast(
		// 	safeRedirect(redirectTo),
		// 	{ title: 'Welcome', description: 'Thanks for signing up!' },
		// 	{ headers },
		// )
		}

	  case InviteCodeLaterFormIntent: {
		const deviceInfo = `${getSourceDeviceType(request)} - ${request.headers.get('user-agent')}`

		const gmailUserId = await getGmailUserId(request)
		
		const gmailUser = gmailUserId
				? await prisma.gmailUser.findUnique({
					select: {
						id: true,
						name: true,
						givenName: true,
						email: true,
						picture: true,
					},
					where: { id: gmailUserId }
	
				})
				: null

		const submission = await parse(formData, {
			schema: intent =>
				SignupFormSchema.superRefine(async (data, ctx) => {
					// check if user exist
					// const existingUser = await prisma.user.findUnique({
					// 	where: { mobileNo: data.mobileNo },
					// 	select: { id: true },
					// })
					// if (existingUser) {
					// 	ctx.addIssue({
					// 		path: ['mobile no.'],
					// 		code: z.ZodIssueCode.custom,
					// 		message: 'A user already exists with this mobile no',
					// 	})
					// 	return
					// }
					
				}).transform(async data => {
					if (intent !== 'submit') return { ...data, session: null }

					const session = await signupWithMobileNo({ ...data, mobileNo, email: data.email ?? null, referralCode: data.referralCode ?? '', gmailUser, isToGenerateReferralCode: false, deviceInfo: deviceInfo})

					//slack notification
					await sendUserOnboardedWithFDPortfolioAndInterestRateSlackMessage(data.name, `${data.fdPortfolioSize}`, `${data.fdPortfolioInterest}`)

					// const session = await signup({ ...data, mobileNo, referralCode: data.referralCode ?? '' })
					return { ...data, session }
				}),
			async: true,
		})

		if (submission.intent !== 'submit') {
			return json({ status: 'idle', submission } as const)
		}

		if(submission.intent === 'submit' && submission.error.hasOwnProperty('fdPortfolioInterest')){

			//submission.error[''] = [response?.msg ?? 'Something Wrong, try again later.']
			submission.error[''] = [submission.error['fdPortfolioInterest'][0]]
			return json({ status: 'error', submission } as const, { status: 400 })
		}

		if (!submission.value?.session) {
			return json({ status: 'error', submission } as const, { status: 400 })
		}

		const { session, redirectTo } = submission.value


		const authSession = await authSessionStorage.getSession(
			request.headers.get('cookie'),
		)

		authSession.set(sessionKey, session.id)
		const verifySession = await verifySessionStorage.getSession()
		const headers = new Headers()
		headers.append(
			'set-cookie',
			await authSessionStorage.commitSession(authSession, {
				expires: session.expirationDate
			}),
		)
		headers.append(
			'set-cookie',
			await verifySessionStorage.destroySession(verifySession),
		)

		return redirect(safeRedirect(redirectTo), {headers})


	}

	}

	return null
}

export async function handleVerification({ submission, user }: VerifyFunctionArgs) {
	invariant(submission.value, 'submission.value should be defined by now')
	const verifySession = await verifySessionStorage.getSession()
	verifySession.set(onboardingMobileSessionKey, submission.value.target)
	return redirect(submission.value[redirectToQueryParam] ? `/onboarding?redirectTo=${submission.value[redirectToQueryParam].toString()}` : '/onboarding', {
		headers: {
			'set-cookie': await verifySessionStorage.commitSession(verifySession),
		},
	})
}

export const meta: MetaFunction = () => {
	return [{ title: 'Onboarding | SuperFD' }]
}

async function NameFormAction({ formData }: NameFormActionArgs) {

    const submission = await parse(formData, { 
		schema: intent =>
			 GenerateInviteCodeSchema.superRefine(async (data, ctx) => {
				// check if the referral code is valid, if it's available
				if(data.referralCode && data.referralCode !== ''){
					const checkCode = await prisma.user.findFirst({
						where: { referralCode: data.referralCode.toUpperCase().trim() }
					})
					if(!checkCode){
						ctx.addIssue({
							path: ['referralCode'],
							code: z.ZodIssueCode.custom,
							message: 'Invite Code is not valid'
						})
						return
					}	
				}
			 }),
			 async: true
	} )

    if (!submission.value) {
      return json({ status: 'error', submission, msg: 'Error' } as const, { status: 400 })
    }

    const { name, referralCode } = submission.value

    return json({ status: 'success', submission, data: { name, referralCode }} as const)
	
}

async function GenerateInviteCodeAction({ request, formData }: NameFormActionArgs) {
	const deviceInfo = `${getSourceDeviceType(request)} - ${request.headers.get('user-agent')}`

	const gmailUserId = await getGmailUserId(request)
		
		const gmailUser = gmailUserId
				? await prisma.gmailUser.findUnique({
					select: {
						id: true,
						name: true,
						givenName: true,
						email: true,
						picture: true,
					},
					where: { id: gmailUserId }
	
				})
				: null

    const submission = await parse(formData, {
			schema: intent =>
				NameAndInviteCodeFormSchema.superRefine(async (data, ctx) => {
					// check if the referral code is valid, if it's available
					if(data.referralCode && data.referralCode !== ''){
						const checkCode = await prisma.user.findFirst({
							where: { referralCode: data.referralCode.toUpperCase().trim() }
						})
						if(!checkCode){
							ctx.addIssue({
								path: ['referralCode'],
								code: z.ZodIssueCode.custom,
								message: 'Invite Code is not valid'
							})
							return
						}	
					}
					
				}).transform(async data => {
					if (intent !== 'submit') return { ...data, session: null }

					const session = await signupWithMobileNo({ ...data, email: data.email ?? null, age: 0, referralCode: data.referralCode ?? '', fdPortfolioSize: 1,fdPortfolioInterest: 4, isNewFdUser: 'false', gmailUser, isToGenerateReferralCode: false, deviceInfo: deviceInfo})

					// const session = await signup({ ...data, mobileNo, referralCode: data.referralCode ?? '' })

					//slack notification
					await sendUserOnboardedWithInviteCodeSlackMessage(data.name, data.referralCode)

					return { ...data, session }
					// return { ...data }
				}),
			async: true,
		})

    if (!submission.value) {
      return json({ status: 'error', submission, msg: 'Error' } as const, { status: 400 })
    }

	if (!submission.value?.session) {
		return json({ status: 'error', submission, data: { name } } as const, { status: 400 })
	}

    const { session, redirectTo } = submission.value

	const authSession = await authSessionStorage.getSession(
		request.headers.get('cookie'),
	)

	authSession.set(sessionKey, session.id)
	const verifySession = await verifySessionStorage.getSession()
	const headers = new Headers()
	headers.append(
		'set-cookie',
		await authSessionStorage.commitSession(authSession, {
			expires: session.expirationDate
		}),
	)
	headers.append(
		'set-cookie',
		await verifySessionStorage.destroySession(verifySession),
	)

	return redirect(safeRedirect(redirectTo), {headers})

    //return json({ status: 'success', submission, data: { name, referralCode, email }} as const)
	
}


export default function SignupRoute() {
	const data = useLoaderData<typeof loader>()
	const isPending = useIsPending()
	const gmailUser = useOptionalGmailUser()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

    const fetcher = useFetcher<typeof NameFormAction>()
	// const [portfolio, setPortfolio] = useState<number[]>([defaultCurrentPortfolio])
	// const [interest, setInterest] = useState<number[]>([defaultCurrentInterest])
	// const [interestInput, setInterestInput] = useState<string>(`${defaultCurrentInterest}`)

	// const [referralCode, setReferralCode] = useState<string>('')

	const [form, fields] = useForm({
		id: 'name-form',  
		constraint: getFieldsetConstraint(NameFormSchema),
		defaultValue: { 
			referralCode: '',
			redirectTo, 
			mobileNo: data.mobileNo,
			name: gmailUser?.name ?? '',
			email: gmailUser?.email ?? ''
		},
		lastSubmission: fetcher.data?.submission,
		// onValidate({ formData }) {
		// 	return parse(formData, { schema: SignupFormSchema })
		// },
		shouldRevalidate: 'onBlur',
	})

	if(fetcher.data && fetcher.data.status === 'success' && fetcher.data.data && fetcher.data.data.name ){
		return (
			<div>
			<FdPortfolioRoute name={fetcher.data.data.name} referralCode={fetcher.data.data.referralCode ?? ''} />
			</div>
		)
		
	}

	return (
			<div className="container flex min-h-full flex-col justify-center pb-32 pt-0 lg:pt-20 px-0">
			<div className="mx-auto w-full max-w-lg">
				<div className="flex flex-col gap-3 text-center">
					<h1 className="text-h3 md:text-h1 lg:text-5xl">Welcome to SuperFD</h1>
					{/* <p className="text-body-md text-muted-foreground">
						Please enter your details.
					</p> */}
				</div>
				<Spacer size="xs" />
				<fetcher.Form
					method="POST"
					className="mx-auto min-w-full max-w-sm sm:min-w-[368px]"
					{...form.props}
				>
					<AuthenticityTokenInput />
					<HoneypotInputs />
		
					<Field
						labelProps={{ htmlFor: fields.name.id, children: 'Name' }}
						inputProps={{
							...conform.input(fields.name),
							autoFocus: true,
							autoComplete: 'name',
						}}
						errors={fields.name.errors}
					/>
					{/* <Field
						labelProps={{ htmlFor: fields.email.id, children: 'Email ( optional )' }}
						inputProps={{
							...conform.input(fields.email),
							autoFocus: false,
							autoComplete: 'email',
						}}
						errors={fields.email.errors}
					/> */}
                    <Field
						labelProps={{ htmlFor: fields.referralCode.id, children: 'Referral Code ( optional )' }}
						inputProps={{
							...conform.input(fields.referralCode),
							autoFocus: false,
							autoComplete: 'referral-code',
						}}
						errors={fields.referralCode.errors}
					/>

                    <div className="">
						<div >
							{/* <Label htmlFor={'offer-code'} children="Invite Code" />
							<Input
								id={'offer-code'}
								autoComplete="referral-code"
								className="my-1 w-full"
								name="referralCode"
								value={referralCode}
								onChange={(e) => setReferralCode(e.target.value)}
							/> */}
							{/* {
								(referralCode === 'SUP100' || referralCode === 'sup100') && (
									<>
									<p
										className="text-muted-foreground mt-2 text-xs"
										role="region"
										aria-live="polite"
									>
										<span className="font-semibold">Congrats!</span> Your SUP100 code is applied. Enjoy your ₹1000 Cashback.
									</p>
									</>
								)
							}
							{
								(referralCode === 'SUP250' || referralCode === 'sup250') && (
									<>
									<p
										className="text-muted-foreground mt-2 text-xs"
										role="region"
										aria-live="polite"
									>
										<span className="font-semibold">Congrats!</span> Your SUP250 code is applied. Enjoy your ₹250 Cashback.
									</p>
									</>
								)
							} */}
							{/* <div className={true ? "min-h-[32px] px-4 pb-3 pt-1" : "hidden"}>
								{fields.referralCode.errors?.length  ? <ErrorList id={fields.referralCode.errors?.length ? `offer-code-error` : undefined} errors={fields.referralCode.errors} /> : null}
							</div> */}
							
						</div>
					</div>


					{/* <div className="pb-10">
						    <Label className="">Available Offer Code</Label>
					        <div className="w-full mx-auto py-2">
								<div className="bg-gradient-to-br from-violet-500 to-purple-500 text-white text-center py-4 px-8 md:px-10 rounded-lg shadow-md relative">
									<div className="flex justify-between items-center">
									  <div>
										<img src={AmazonLogo} className="w-10 inline mx-auto mr-1 rounded-lg" alt="amazon logo" />
										<span id="cpnCode" className="border-dashed border-2 font-bold border-white text-white px-4 py-2">SUP250</span>
									  </div>

									  <div>
									  <button onClick={() => setReferralCode('SUP250')} id="cpnBtn" className="border border-white bg-white text-purple-600 px-4 py-2 rounded-md text-sm font-medium cursor-pointer">{referralCode === 'SUP250' ? 'APPLIED' : 'APPLY'}</button>
									  </div>
									  
									</div>
									
									<h3 className="text-xs font-semibold my-2">Book Suryoday FD within 24 hours of sign up to get assured ₹250 Cashback.</h3>
									
									<div className="w-12 h-12 bg-background rounded-full absolute top-1/2 transform -translate-y-1/2 left-0 -ml-6"></div>
									<div className="w-12 h-12 bg-background rounded-full absolute top-1/2 transform -translate-y-1/2 right-0 -mr-6"></div>

								</div>
							</div>
					</div> */}

					<ErrorList errors={form.errors} id={form.errorId} />

					{/* <input {...conform.input(fields.mobileNo, { type: 'hidden' })} />
					<input {...conform.input(fields.redirectTo, { type: 'hidden' })} />
					<input {...conform.input(fields.age, { type: 'hidden' })} /> */}

					<input type="hidden" name="mobileNo" value={data.mobileNo} defaultValue={data.mobileNo} />
					<input type="hidden" name="redirectTo" value={redirectTo ?? ''} defaultValue={redirectTo ?? ''} />

					{/* <div className="flex items-center justify-between gap-6 my-4">
						<StatusButton
							className="w-full"
							variant={"outline"}
							type="submit"
							name="intent"
							value={GenerateInviteCodeIntent}
							disabled={isPending}
                            status={fetcher.state !== 'idle' ? 'pending' : 'idle'}
						>
                        Generate Invite Code				
						</StatusButton>
					</div> */}

					<div className="flex items-center justify-between gap-6">
						<StatusButton
							className="w-full"
							type="submit"
							name="intent"
							value={NameFormIntent}
							disabled={isPending}
                            status={fetcher.state !== 'idle' ? 'pending' : 'idle'}
						>
                        Next						
						</StatusButton>
					</div>

										
				</fetcher.Form>
			</div>
		</div>	
	)

}

async function FortfolioFormAction({ formData }: NameFormActionArgs) {

    const submission = parse(formData, { schema: SignupFormSchema} )

    if (!submission.value) {
      return json({ status: 'error', submission, msg: 'Error' } as const, { status: 400 })
    }

	await prisma.user.update({
		where: { mobileNo: submission.value?.mobileNo},
		data: {
			name: submission.value?.name ?? 'username',
			fdPortfolioSize: submission.value?.fdPortfolioSize,
			fdPortfolioInterest: submission.value?.fdPortfolioInterest
		},
		select: {
			id: true,
		}
	})

	await sendUserOnboardedWithoutInviteCodeSlackMessage(submission.value.name ?? 'username', `${submission.value?.fdPortfolioSize}`)

    return json({ status: 'success', submission, data: submission.value } as const)
	
}


function FdPortfolioRoute( { name, referralCode }: { name: string, referralCode: string }) {
	const data = useLoaderData<typeof loader>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [portfolio, setPortfolio] = useState<number[]>([defaultCurrentPortfolio])
	const [interest, setInterest] = useState<number[]>([defaultCurrentInterest])
	//const [interestInput, setInterestInput] = useState<string>(`${defaultCurrentInterest}`)
	const [isNewFdUser] = useState<boolean>(false)

	// const [amount, setAmount] = useState<string>(() => {
	// 	return `${new Intl.NumberFormat('en-In').format(parseInt(FD_PORTFOLIO_DEFAULT_AMOUNT))}`
	// })
	// const [amtError, setAmtError] = useState<string[]>(() => {

	// 	if( MIN_FD_PORTFOLIO_AMOUNT && (amount === '' || (`${new Intl.NumberFormat('en-In').format(parseInt((amount).replaceAll(',','')))}` === 'NaN')) ){
	// 	return [`Min. Portfolio Amount is Rs. ${new Intl.NumberFormat('en-In').format(parseInt((MIN_FD_PORTFOLIO_AMOUNT.toString()).replaceAll(',','')))}`]
	// 	}else{

	// 		// if(MIN_FD_PORTFOLIO_AMOUNT && Number((amount).replaceAll(',','')) < MIN_FD_PORTFOLIO_AMOUNT){
	// 		// 	return [`Min. Amount is Rs. ${new Intl.NumberFormat('en-In').format(parseInt((MIN_FD_PORTFOLIO_AMOUNT.toString()).replaceAll(',','')))}`]
	// 		// }
	// 		if(MAX_FD_PORTFOLIO_AMOUNT && Number((amount).replaceAll(',','')) > MAX_FD_PORTFOLIO_AMOUNT){
	// 			return [`Max. Portfolio Amount is Rs. ${new Intl.NumberFormat('en-In').format(parseInt((MAX_FD_PORTFOLIO_AMOUNT.toString()).replaceAll(',','')))}`]
	// 		}else{
	// 			return []
	// 		}
	// 	}
	// })

	const fetcher = useFetcher<typeof FortfolioFormAction>()

	//console.log('fetcher : ', fetcher.data)

	const [form, fields] = useForm({
		id: 'portfolio-form',
		constraint: getFieldsetConstraint(SignupFormSchema),
		defaultValue: { redirectTo, mobileNo: data.mobileNo, age: 0, name, referralCode },
		lastSubmission: fetcher.data?.submission,
		// onValidate({ formData }) {
		// 	return parse(formData, { schema: SignupFormSchema })
		// },
		shouldRevalidate: 'onBlur',
	})

	// if(fetcher.data && fetcher.data.status === 'success' && fetcher.data.data && fetcher.data.data.name ){
	// 	return (
	// 		<div>
	// 		<GenerateInviteCodeRoute {...fetcher.data.data} />
	// 		</div>
	// 	)
		
	// }

	return (
		<div className="container flex min-h-full flex-col justify-center pb-32 pt-0 lg:pt-20 px-0">
			<div className="mx-auto w-full max-w-lg">
				<div className="flex flex-col gap-3 text-center">
					<h1 className="text-h3 md:text-h1 lg:text-5xl">Welcome to SuperFD</h1>
					{/* <p className="text-body-md text-muted-foreground">
						Please enter your details.
					</p> */}
				</div>
				<Spacer size="xs" />
				<fetcher.Form
					method="POST"
					className="mx-auto min-w-full max-w-sm sm:min-w-[368px]"
					{...form.props}
				>
					<AuthenticityTokenInput />
					<HoneypotInputs />
		
					{/* <Field
						labelProps={{ htmlFor: fields.name.id, children: 'Full Name' }}
						inputProps={{
							...conform.input(fields.name),
							autoFocus: true,
							autoComplete: 'name',
						}}
						errors={fields.name.errors}
					/>
                    <Field
						labelProps={{ htmlFor: fields.referralCode.id, children: 'Referral Code ( optional )' }}
						inputProps={{
							...conform.input(fields.referralCode),
							autoFocus: false,
							autoComplete: 'referral-code',
						}}
						errors={fields.referralCode.errors}
					/> */}
					{/* <Field
						labelProps={{ htmlFor: fields.age.id, children: 'Age' }}
						inputProps={{
							...conform.input(fields.age, { type: 'Age' }),
							autoComplete: 'age',
						}}
						errors={fields.age.errors}
					/> */}

					<div className="pb-6">
						<Label>FD Portfolio</Label>
						<CardDescription>Your existing FD portfolio amount</CardDescription>
						<Slider
						defaultValue={[defaultCurrentPortfolio]}
						min={MIN_PORTFOLIO}
						max={MAX_PORTFOLIO}
						step={1}
						value={portfolio}
						className="w-full pt-6 pb-4"
						onValueChange={(val) => setPortfolio(val)}
						/>
						<div className="flex gap-2 items-center text-sm text-muted-foreground">
							{/* <Input
								type="text" 
								className="w-[30%]"
								value={portfolio[0]}
								onChange={(e) => setPortfolio(() => {
									if(Number(e.target.value) > MAX_PORTFOLIO){
										return [MAX_PORTFOLIO]
									}else{
                                        return [Number(e.target.value)]
									}
								})}
							/> */}
							{portfolio[0]}
							<div className="">
								{
									portfolio[0] === MAX_PORTFOLIO
										? 'Lakhs+' 
										: portfolio[0] === 1 ? 'Lakh' : 'Lakhs'
								}
							</div>
						</div>

						{ /* 

						<Input 
							type="text" 
							id="amount" 
							placeholder="Enter the amount"
							className="focus-visible:ring-gain-light"
							name="amount" 
							defaultValue={FD_PORTFOLIO_DEFAULT_AMOUNT}
							value={amount}
							min={MIN_FD_PORTFOLIO_AMOUNT}
							maxLength={15}
							onChange={e => {
							if( MIN_FD_PORTFOLIO_AMOUNT  && (e.target.value === '' || (`${new Intl.NumberFormat('en-In').format(parseInt((e.target.value).replaceAll(',','')))}` === 'NaN')) ){
								setAmount('')
								//setAmtError([`Min. Portfolio Amount is Rs. ${new Intl.NumberFormat('en-In').format(parseInt((MIN_FD_PORTFOLIO_AMOUNT .toString()).replaceAll(',','')))}`])
								setAmtError([`Please enter your FD Portfolio Amount`])
							}else{
								//console.log('value, amount : ', e.target.value, amount)
								// if(MIN_FD_PORTFOLIO_AMOUNT  && Number((e.target.value).replaceAll(',','')) < MIN_FD_PORTFOLIO_AMOUNT ){
								// setAmtError([`Min. Portfolio Amount is Rs. ${new Intl.NumberFormat('en-In').format(parseInt((MIN_FD_PORTFOLIO_AMOUNT .toString()).replaceAll(',','')))}`])
								// }else if(MAX_FD_PORTFOLIO_AMOUNT && Number((e.target.value).replaceAll(',','')) > MAX_FD_PORTFOLIO_AMOUNT){
								// setAmtError([`Max. Portfolio Amount is Rs. ${new Intl.NumberFormat('en-In').format(parseInt((MAX_FD_PORTFOLIO_AMOUNT.toString()).replaceAll(',','')))}`])
								// }else{
								setAmtError([])
								//}
								setAmount(`${new Intl.NumberFormat('en-In').format(parseInt((e.target.value).replaceAll(',','')))}`)
							}
							

							}}
						/>
						<div className="min-h-[16px] px-4 pb-1 pt-1">
							{
							amtError.length > 0 
								? <ErrorListCalculator errors={amtError} /> 
								: (
								<p className="text-xs text-muted-foreground">
									{toWords.convert(parseInt(amount.replaceAll(',','')))}
								</p>
								)
							}
						</div>

						*/}
						

					</div>

					<div className="pt-2 pb-6">
						<Label>{"Interest Rate"}</Label>
						<CardDescription>Average interest rate on your existing FD</CardDescription>
						<Slider
						defaultValue={[defaultCurrentInterest]}
						min={MIN_INTEREST}
						max={MAX_INTEREST}
						step={0.1}
						className="w-full pt-6 pb-4"
						value={interest}
						onValueChange={(val) => {
							setInterest(val)
							//setInterestInput(`${val[0]}`)
						}}
						/>
						<div className="flex gap-2 items-center text-sm text-muted-foreground">
							{/* <Input
								type="text" 
								className="w-[30%]"
								value={interestInput}
								min={MIN_INTEREST}
								max={MAX_INTEREST}
								onChange={(e) => {
                                    let val = e.target.value
									setInterestInput(val)
									if(val.includes('.')){
										if(val.split('.')[1]){
											setInterest([Number(val)])
										}
									}else{
										setInterest([Number(val)])
									}
									if(Number(interest[0]) > MAX_INTEREST){
                                       setInterest([MAX_INTEREST])
									}
									if(Number(interest[0]) < MIN_INTEREST){
										setInterest([MIN_INTEREST])
									}
									
								}}
							/> */}
							{interest}
							<div className="">
								%
							</div>
						</div>
						

					</div>

					{/* <div className="w-full pt-2 pb-6">

					<div className="py-2 flex space-x-2">
					<Checkbox id="new_fd_user" defaultChecked={isNewFdUser} onCheckedChange={e => setIsNewFdUser(current => !current)} />
					<Label htmlFor="new_fd_user" className="font-light">I'm new to FDs</Label>
					</div>

					</div> */}

					
					<input {...conform.input(fields.name, { type: 'hidden' })} />
					<input {...conform.input(fields.email, { type: 'hidden' })} />
					<input {...conform.input(fields.referralCode, { type: 'hidden' })} />
					<input {...conform.input(fields.mobileNo, { type: 'hidden' })} />
					<input {...conform.input(fields.redirectTo, { type: 'hidden' })} />
					<input {...conform.input(fields.age, { type: 'hidden' })} />
					<input type="hidden" name="fdPortfolioSize" value={portfolio[0]} />
					<input  type="hidden" name="fdPortfolioInterest" value={interest[0]}  />
					<input  type="hidden" name="isNewFdUser" value={`${isNewFdUser}`}  />
					<ErrorList errors={form.errors} id={form.errorId} />

					<div className="flex items-center justify-between gap-6">
						<StatusButton
							className="w-full"
							status={fetcher.state !== 'idle' ? 'pending' : 'idle'}
							type="submit"
							disabled={isPending}
							//disabled={!( Number((amount).replaceAll(',','')) >= MIN_FD_PORTFOLIO_AMOUNT && Number((amount).replaceAll(',','')) < MAX_FD_PORTFOLIO_AMOUNT ) }
							name="intent"
							value={SignupFormIntent}
						>
                         Next						
						</StatusButton>
					</div>
				</fetcher.Form>
			</div>
		</div>
	)
}

// function GenerateInviteCodeRoute( { name, mobileNo, age, fdPortfolioSize, fdPortfolioInterest, email, isNewFdUser, referralCode, redirectTo }: { 
// 	name: string;
//     mobileNo: string;
//     age: number;
//     fdPortfolioSize: number;
//     fdPortfolioInterest: number;
//     isNewFdUser: "true" | "false";
//     email?: string | undefined;
//     referralCode?: string | undefined;
//     redirectTo?: string | undefined }
// ) {
// 	const actionData = useActionData<typeof action>()
// 	const isPending = useIsPending()

// 	const [isMinFDPassed] = useState<boolean>(() => {
// 		if(fdPortfolioSize > MIN_FD_PORTFOLIO){
// 			return true
// 		}else{
// 			return false
// 		}
// 	})
// 	const [form, fields] = useForm({
// 		id: 'onboarding-form',
// 		constraint: getFieldsetConstraint(SignupFormSchema),
// 		defaultValue: { redirectTo, mobileNo, age, fdPortfolioSize, fdPortfolioInterest, email, referralCode, isNewFdUser, name },
// 		lastSubmission: actionData?.submission,
// 		// onValidate({ formData }) {
// 		// 	return parse(formData, { schema: SignupFormSchema })
// 		// },
// 		shouldRevalidate: 'onBlur',
// 	})

// 	return (
// 		<div className="container flex min-h-full flex-col justify-center pb-32 pt-0 lg:pt-20 px-0">
// 			<div className="mx-auto w-full max-w-lg">
// 				<div className="flex flex-col gap-3 text-center">
// 					<h1 className="text-h3 md:text-h1 lg:text-5xl">Welcome to SuperFD</h1>
// 					{/* <p className="text-body-md text-muted-foreground">
// 						Please enter your details.
// 					</p> */}
// 				</div>
// 				<Spacer size="xs" />
// 				<Form
// 					method="POST"
// 					className="mx-auto min-w-full max-w-sm sm:min-w-[368px]"
// 					{...form.props}
// 				>
// 					<AuthenticityTokenInput />
// 					<HoneypotInputs />

// 					{
// 						isMinFDPassed ? (
//                             <>
// 							 <div>
// 								<p className="text-sm md:text-lg text-muted-foreground leading-relaxed text-balance">
// 									Invite code has a validity of 7 days and can be generated only twice per user. If you are not ready to invest 1L+, you can generate an invite code later.
// 								</p>
// 							 </div>
// 							</>
// 						) : (
// 							<>
// 							<p className="text-sm md:text-lg text-muted-foreground leading-relaxed text-balance">
// 							Sorry, we are currently onboarding only users with an existing FD portfolio of ₹2L+. You have been added to our waitlist and we will notify you once we open access for everyone.
// 							</p>
// 							</>
// 						)
// 					}

// 					<Spacer size="xs" />
					
// 					<input {...conform.input(fields.name, { type: 'hidden' })} />
// 					<input {...conform.input(fields.email, { type: 'hidden' })} />
// 					<input {...conform.input(fields.referralCode, { type: 'hidden' })} />
// 					<input {...conform.input(fields.mobileNo, { type: 'hidden' })} />
// 					<input {...conform.input(fields.redirectTo, { type: 'hidden' })} />
// 					<input {...conform.input(fields.age, { type: 'hidden' })} />
// 					<input {...conform.input(fields.fdPortfolioInterest, { type: 'hidden' })} />
// 					<input {...conform.input(fields.fdPortfolioSize, { type: 'hidden' })} />
// 					<input {...conform.input(fields.isNewFdUser, { type: 'hidden' })} />
					
// 					<ErrorList errors={form.errors} id={form.errorId} />

					

// 					<div className="flex flex-col items-center justify-between gap-6">
// 						{
// 							isMinFDPassed ? (
// 								<>
// 								<input type="hidden" name="isInviteGenerate" value={"true"} defaultValue={"true"} />
// 								<StatusButton
// 									className="w-full"
// 									status={isPending ? 'pending' : actionData?.status ?? 'idle'}
// 									type="submit"
// 									disabled={isPending}
// 									name="intent"
// 									value={SignupFormIntent}
// 								>
// 								Get Invite Code now				
// 								</StatusButton>

// 								<StatusButton
// 									className="w-full"
// 									variant={"outline"}
// 									status={isPending ? 'pending' : actionData?.status ?? 'idle'}
// 									type="submit"
// 									disabled={isPending}
// 									name="intent"
// 									value={InviteCodeLaterFormIntent}
// 								>
// 								Do it later					
// 								</StatusButton>
// 								</>
// 							) : (
// 								<StatusButton
// 									className="w-full"
// 									status={isPending ? 'pending' : actionData?.status ?? 'idle'}
// 									type="submit"
// 									disabled={isPending}
// 									name="intent"
// 									value={HomeRouteFormIntent}
// 								>
// 								Home			
// 								</StatusButton>
// 							)
// 						}
						
// 					</div>
// 				</Form>
// 			</div>
// 		</div>
// 	)
// }

// async function FortfolioFormAction({ formData }: NameFormActionArgs) {

//     const submission = await parse(formData, {
// 			schema: intent =>
// 				NameFormSchema.superRefine(async (data, ctx) => {
// 					// check if the referral code is valid, if it's available
// 					if(data.referralCode && data.referralCode !== ''){
// 						const checkCode = await prisma.user.findFirst({
// 							where: { referralCode: data.referralCode.toUpperCase().trim() }
// 						})
// 						if(!checkCode){
// 							ctx.addIssue({
// 								path: ['referralCode'],
// 								code: z.ZodIssueCode.custom,
// 								message: 'Invite Code is not valid'
// 							})
// 							return
// 						}	
// 					}
					
// 				}).transform(async data => {
// 					return { ...data }
// 				}),
// 			async: true,
// 		})

//     if (!submission.value) {
//       return json({ status: 'error', submission, msg: 'Error' } as const, { status: 400 })
//     }

//     const { name, referralCode, email } = submission.value

//     return json({ status: 'success', submission, data: { name, referralCode, email }} as const)
	
// }